import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { useLocalName } from "@/shared/localName";
import { Button, Modal } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { useMoney } from "@/shared/ui";
import { usePaperTill } from "@/shared/posTheme";
import { PaperDishThumb } from "./PaperMenu";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import type { AddonGroup, MenuItem } from "@/lib/api/types";
import {
  quoteLineTotal,
  resolveUnitChargedPrice,
} from "@/modules/pos/lib/pricing";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/**
 * What the PAPER picker had chosen when it stepped aside for the price or the
 * swap dialog — handed back on its return so nothing is asked twice.
 */
export interface AddonPickerResume {
  counts: Record<string, number>;
  quantity: number;
}

/** The server's ceiling on one line (`MAX_LINE_QUANTITY`). */
const MAX_PLATES = 100;

/**
 * PAPER: which groups ask as the design's SIZE cards — a single choice that
 * must be made (or a VARIANT, which names the version sold). They come first,
 * then the remaining required groups, then the optional ones, each group in
 * its own order. Exported for its spec.
 */
export function paperGroupOrder(groups: readonly AddonGroup[]): {
  group: AddonGroup;
  shape: "cards" | "rows";
}[] {
  const cards = (group: AddonGroup) =>
    group.maxSelect === 1 && (group.minSelect >= 1 || group.kind === "VARIANT");
  return [
    ...groups.filter(cards).map((group) => ({ group, shape: "cards" as const })),
    ...groups
      .filter((group) => !cards(group) && group.minSelect > 0)
      .map((group) => ({ group, shape: "rows" as const })),
    ...groups
      .filter((group) => !cards(group) && group.minSelect <= 0)
      .map((group) => ({ group, shape: "rows" as const })),
  ];
}

/**
 * Addon selection for one menu item, asked before the line is staged.
 *
 * `minSelect`/`maxSelect` are honoured here, but the server validates the same
 * rules on every append — this is convenience, not the guarantee. A "choose
 * exactly one size" rule enforced only in a client that can be opened in
 * devtools is not a rule.
 *
 * It asks up front rather than after staging because the mock's menu tile adds
 * straight to the order: there is no staging tray to go back and fix, so the
 * question has to come at the tap.
 */
export function AddonPicker({
  item,
  groups,
  discountPercent,
  blockedAddonIds,
  substitutionCharge = 0,
  withQuantity = false,
  resume = null,
  unitPrice,
  onOverride,
  onSwap,
  swapSet = false,
  onMarkSoldOut,
  updating = false,
  optionsOnly = false,
  onCancel,
  onConfirm,
}: {
  item: MenuItem;
  groups: AddonGroup[];
  /**
   * The invoice's tier, 0–100. The picker prices what the PLATE will cost on
   * THIS bill, not what the menu says: on a −20% tier it used to promise the
   * list price (155) for a line the invoice would show at 127.
   */
  discountPercent: number;
  /**
   * Options a swap the waiter just confirmed has ruled out (B21).
   *
   * The server refuses these pairings either way — an option that removes what
   * the swap removes is `DUPLICATE_REMOVAL`, one that removes what it adds is
   * `CHAINED_SWAP` — but it refuses the whole append, after the guest has been
   * told yes. Disabled here so the collision is visible on the screen the
   * waiter is standing on. Empty on every ordinary tap.
   */
  blockedAddonIds?: ReadonlySet<string>;
  /**
   * What an open swap confirmed a moment ago will add to this plate (D108).
   *
   * Zero on an ordinary tap. It exists because this picker's total is the LAST
   * number a waiter reads before the plate is committed, and on a dish with
   * option groups the swap dialog has already run — so a total without it
   * states a price the bill will not take.
   */
  substitutionCharge?: number;
  /**
   * PAPER: the dish QUANTITY stepper in the foot — N identical plates in one
   * add. Only where the caller can add N (`onConfirm`'s second argument).
   */
  withQuantity?: boolean;
  /** PAPER: what the picker had chosen before it stepped aside (see `onOverride`). */
  resume?: AddonPickerResume | null;
  /**
   * PAPER: a price typed by hand for this plate (D46), waiting for this
   * picker's confirm. Replaces the LIST price in the quote, as on the server.
   */
  unitPrice?: number;
  /**
   * PAPER: "Different price" (D46) — the price override, moved off the dish
   * tile into this picker. `undefined` without `RESTAURANT_INVOICE_PRICE_OVERRIDE`.
   */
  onOverride?: (resume: AddonPickerResume) => void;
  /**
   * PAPER: "Swap out" (D61) — the ingredient swap, moved off the dish tile
   * into this picker. `undefined` without `RESTAURANT_INVOICE_SUBSTITUTE`, and
   * on a combo, where the server refuses a swap.
   */
  onSwap?: (resume: AddonPickerResume) => void;
  /** PAPER: a swap is already set on the plate being asked about. */
  swapSet?: boolean;
  /**
   * PAPER: "Mark as out" (§5.8, D32) — 86 at the till, moved off the dish
   * tile into this picker. `undefined` without `MENU_MANAGE` ∥
   * `MENU_AVAILABILITY`. The caller closes the picker and asks first.
   */
  onMarkSoldOut?: () => void;
  /** PAPER: changing a NEW line already on the bill — the add reads "Update". */
  updating?: boolean;
  /**
   * PAPER: opened for a dish with NO option groups — for its quantity and its
   * quiet actions — so an empty list is the dish's own shape, not a failed read.
   */
  optionsOnly?: boolean;
  onCancel: () => void;
  onConfirm: (addonIds: string[], quantity: number) => void;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const localName = useLocalName();
  const money = useMoney();
  /**
   * D127 — the dish's description in the screen's language, so a waiter can
   * read it to a guest who asks what is in it. English falls back to Arabic,
   * the same rule as the name; nothing is drawn when neither was written.
   */
  const description =
    (locale === "en" ? item.descriptionEn || item.descriptionAr : item.descriptionAr) || null;
  const swapCollisionNote = t((m) => m.pos.addonSwapCollision);
  /**
   * How many of each option the guest asked for (S10).
   *
   * A COUNT per id, not a set of ids: "extra chicken ×2" is a real order and
   * the old `string[]` + `includes` toggle could only say on or off. The wire
   * shape is unchanged — `onConfirm` still emits a flat list with repeats,
   * which `addPlates` turns into `[{addonId}, {addonId}]` and the server
   * already sums correctly in both the price and the depletion fold. So this is
   * a picker change, not a contract change.
   */
  const [counts, setCounts] = useState<Record<string, number>>(
    () => resume?.counts ?? {},
  );
  /** PAPER: how many identical plates this add makes. */
  const [quantity, setQuantity] = useState(() => resume?.quantity ?? 1);
  /** What the picker opened with — a line being edited opens on its own picks. */
  const [opened] = useState(() => ({
    counts: resume?.counts ?? {},
    quantity: resume?.quantity ?? 1,
  }));

  const countOf = (addonId: string) => counts[addonId] ?? 0;

  /** Total PICKS in a group — repeats included, which is what the rules bound. */
  const pickedIn = (group: AddonGroup) =>
    group.addons.reduce((sum, addon) => sum + countOf(addon.id), 0);

  const setCount = (addonId: string, next: number) =>
    setCounts((current) => {
      if (next <= 0) {
        const { [addonId]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [addonId]: next };
    });

  const increase = (group: AddonGroup, addonId: string) => {
    // maxSelect 1 still behaves like a radio — replace rather than refuse,
    // which is what a thumb expects from a row of sizes. A size is not
    // something you can have twice.
    if (group.maxSelect === 1) {
      setCounts((current) => {
        const withoutGroup = { ...current };
        for (const addon of group.addons) {
          delete withoutGroup[addon.id];
        }
        return { ...withoutGroup, [addonId]: 1 };
      });
      return;
    }
    // The cap counts TOTAL picks, not distinct options: "at most 2 sauces"
    // must stop at one sauce taken twice, or the rule means nothing.
    if (group.maxSelect !== null && pickedIn(group) >= group.maxSelect) {
      return;
    }
    setCount(addonId, countOf(addonId) + 1);
  };

  const decrease = (addonId: string) => setCount(addonId, countOf(addonId) - 1);

  /** The flat, repeat-carrying list the till sends. */
  const selected = useMemo(
    () =>
      groups.flatMap((group) =>
        group.addons.flatMap((addon) =>
          Array.from({ length: countOf(addon.id) }, () => addon.id),
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, counts],
  );

  /** The tiered base, on its own — the price this plate starts from tonight. */
  const chargedBase = resolveUnitChargedPrice(item.price, discountPercent);

  /**
   * Tiered base + selected modifiers — what this line will actually cost,
   * priced exactly as the server prices it: the tier comes off the ITEM only,
   * addons are added at face value, and every figure is rounded the way the
   * pricing policy rounds it.
   */
  const total = useMemo(() => {
    const addonPrices: number[] = [];
    for (const group of groups) {
      for (const addon of group.addons) {
        // Once PER PICK, so two helpings quote two helpings. Reading
        // `selected.includes` counted a repeated option once and under-quoted
        // the line the server was about to charge for.
        for (let taken = 0; taken < countOf(addon.id); taken += 1) {
          addonPrices.push(addon.price);
        }
      }
    }
    return quoteLineTotal(
      unitPrice ?? item.price,
      discountPercent,
      addonPrices,
      substitutionCharge,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.price, unitPrice, discountPercent, groups, counts, substitutionCharge]);

  /**
   * The item is DEFINED to carry modifier groups (its caller only opens this
   * picker for such an item) and none arrived — the addon-groups read failed,
   * or a group was deleted under the till.
   *
   * That is not "no required choices": with an empty list every `minSelect`
   * check below passes because there is nothing to check, and confirming would
   * send a plate to the kitchen missing the size or the choice it is defined to
   * carry. So an empty list blocks confirm outright rather than sailing through
   * it.
   */
  const groupsMissing = groups.length === 0 && !optionsOnly;

  const unmetGroups = useMemo(
    () =>
      groups.filter((group) => {
        if (group.minSelect <= 0) return false;
        // Counted over total picks, matching the server's own
        // `assertAddonSelectionValid`, which tallies repeated ids.
        return pickedIn(group) < group.minSelect;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, counts],
  );

  // Only a CHANGE is a guest's order half-taken (founder, 2026-10-06). A fresh
  // add with nothing ticked closes on a swipe as it always did, and so does a
  // line opened for Edit and closed as it was: its own picks are not "unsaved
  // changes", and asking to discard them read as if closing would lose them.
  const guard = useDiscardGuard(
    quantity !== opened.quantity || !samePicks(counts, opened.counts),
    onCancel,
  );
  // PAPER (light till): the redesign's picker — the dish's photo at the head,
  // the size asked first as cards, extras as checkbox rows, and the dish's
  // quantity beside the add.
  const phone = useIsMobile();
  const paper = usePaperTill();
  const resumeState = (): AddonPickerResume => ({ counts, quantity });
  const blocked = groupsMissing || unmetGroups.length > 0;
  const confirm = () => onConfirm(selected, paper && withQuantity ? quantity : 1);

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      // "lg" (the default) is the wide form dialog and left the options
      // stranded on one side; "sm" then went too far the other way, crowding
      // chips a thumb has to hit mid-service. "md" is the question-sized one.
      size="md"
      data-testid="addon-picker"
      // The phone head's box is 44px (waiter phone, 2026-10-28); a 56px badge
      // in it was clipped at the edges.
      icon={paper ? <PaperDishThumb item={item} size={phone ? 44 : 56} /> : undefined}
      title={localName(item)}
      // The base as THIS bill will charge it, so the subtitle and the confirm
      // button cannot tell two different stories about the same plate.
      subtitle={
        paper
          ? t((m) => m.paperRegister.basePrice, { amount: money(chargedBase) })
          : `${t((m) => m.pos.basePrice)} ${money(chargedBase)}`
      }
      closeLabel={t((m) => m.common.cancel)}
      // Actions belong in the footer SLOT, not in the body. Passing them as
      // children put them inside the scrolling body card, below the validation
      // note, which is what made the dialog look broken.
      footerActions={
        paper ? (
          <PaperPickerFoot
            withQuantity={withQuantity}
            quantity={quantity}
            onQuantity={setQuantity}
            label={t(
              (m) => (updating ? m.paperRegister.update : m.paperRegister.addToOrder),
              { amount: money(total * (withQuantity ? quantity : 1)) },
            )}
            disabled={blocked}
            onConfirm={confirm}
          />
        ) : (
        <>
          <Button
            variant="outline"
            size="touch"
            onClick={guard.requestClose}
            data-testid="addon-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            variant="primary"
            size="touch"
            disabled={blocked}
            onClick={confirm}
            data-testid="addon-confirm"
          >
            {t((m) => m.pos.addItems)} · {money(total)}
          </Button>
        </>
        )
      }
      footerLeft={
        paper
          ? undefined
          : groupsMissing
          ? t((m) => m.pos.addonsFailedBody)
          : unmetGroups.length > 0
            ? t((m) => m.pos.chooseRequiredAddons)
            : undefined
      }
    >
      {description ? (
        <p
          data-ui="addon-picker-description"
          data-testid="addon-picker-description"
          className="mb-3 text-[length:var(--font-size-xs)] leading-snug text-[color:var(--text-muted)]"
        >
          {description}
        </p>
      ) : null}
      {paper ? (
        <PaperPickerBody
          groups={groups}
          countOf={countOf}
          pickedIn={pickedIn}
          increase={increase}
          decrease={decrease}
          clear={(addonId) => setCount(addonId, 0)}
          blockedAddonIds={blockedAddonIds}
          swapCollisionNote={swapCollisionNote}
          groupsMissing={groupsMissing}
          note={
            groupsMissing
              ? t((m) => m.pos.addonsFailedBody)
              : unmetGroups.length > 0
                ? t((m) => m.pos.chooseRequiredAddons)
                : null
          }
          handPrice={
            unitPrice !== undefined
              ? t((m) => m.paperRegister.handPrice, {
                  amount: money(resolveUnitChargedPrice(unitPrice, discountPercent)),
                })
              : null
          }
          swapSet={swapSet}
          onOverride={onOverride ? () => onOverride(resumeState()) : undefined}
          onSwap={onSwap ? () => onSwap(resumeState()) : undefined}
          onMarkSoldOut={onMarkSoldOut}
        />
      ) : (
      <div className="flex flex-col gap-6 py-1">
        {groupsMissing ? (
          <p className="text-sm font-semibold text-[color:var(--danger)]">
            {t((m) => m.pos.addonsFailedTitle)}
          </p>
        ) : null}
        {groups.map((group) => (
          <fieldset
            key={group.id}
            data-ui="addon-group"
            data-kind={group.kind}
            className="flex flex-col gap-3"
          >
            <legend
              data-ui="addon-legend"
              className="mb-3 text-sm font-bold text-[color:var(--text-secondary)]"
            >
              {localName(group)}
              <AddonGroupCap maxSelect={group.maxSelect} />
              {group.minSelect > 0 ? (
                <span className="ms-1 text-[color:var(--danger)]">
                  {t((m) => m.pos.addonRequired)}
                </span>
              ) : null}
            </legend>
            <div data-ui="addon-options" className="flex flex-wrap gap-2.5">
              {group.addons
                .filter((addon) => addon.isActive)
                .map((addon) => {
                  const taken = countOf(addon.id);
                  const isOn = taken > 0;
                  // A size cannot be taken twice, so a single-select group
                  // stays a plain radio chip. Only a group that ALLOWS more
                  // than one gets the stepper — showing -/+ on a size would
                  // invite a tap that does nothing.
                  const repeatable = group.maxSelect !== 1;
                  const atCap =
                    group.maxSelect !== null && pickedIn(group) >= group.maxSelect;
                  // B21: this option changes the same ingredient the pending
                  // swap does. Not merely disabled — a chip that greys out
                  // with no reason reads as a bug, and the sentence under the
                  // group says which two things collided.
                  const collides = blockedAddonIds?.has(addon.id) ?? false;
                  return (
                    <div
                      key={addon.id}
                      data-ui="addon-option"
                      data-on={isOn ? "" : undefined}
                      className={[
                        "flex items-center gap-1 rounded-full border text-sm font-bold transition-colors",
                        isOn
                          ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
                          : "border-[color:var(--border-strong)] text-[color:var(--text-secondary)] hover:text-[color:var(--text)]",
                        repeatable && isOn ? "ps-1 pe-4" : "px-4",
                      ].join(" ")}
                    >
                      {repeatable && isOn ? (
                        <button
                          type="button"
                          aria-label={`${t((m) => m.pos.addonDecrease)} ${localName(addon)}`}
                          onClick={() => decrease(addon.id)}
                          data-testid={`addon-minus-${addon.id}`}
                          // 44px: this sits beside another control and is
                          // tapped mid-service with a thumb.
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base"
                        >
                          −
                        </button>
                      ) : null}
                      <button
                        type="button"
                        aria-pressed={isOn}
                        onClick={() =>
                          isOn && !repeatable
                            ? decrease(addon.id)
                            : increase(group, addon.id)
                        }
                        disabled={collides || (!isOn && atCap)}
                        data-testid={`addon-${addon.id}`}
                        title={collides ? swapCollisionNote : undefined}
                        className="min-h-11 py-2.5 disabled:opacity-40"
                      >
                        {localName(addon)}
                        {addon.price > 0 ? (
                          <span data-ui="addon-price" className="ms-1.5 tabular-nums opacity-80">
                            +{money(addon.price)}
                          </span>
                        ) : null}
                        {/* The count rides in the chip only once it is more
                            than one: a lone "×1" on every picked option is
                            noise on a screen read at arm's length. */}
                        {taken > 1 ? (
                          <span
                            className="ms-1.5 tabular-nums"
                            data-testid={`addon-count-${addon.id}`}
                          >
                            ×{taken}
                          </span>
                        ) : null}
                      </button>
                      {repeatable && isOn ? (
                        <button
                          type="button"
                          aria-label={`${t((m) => m.pos.addonIncrease)} ${localName(addon)}`}
                          onClick={() => increase(group, addon.id)}
                          disabled={atCap}
                          data-testid={`addon-plus-${addon.id}`}
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base disabled:opacity-40"
                        >
                          +
                        </button>
                      ) : null}
                    </div>
                  );
                })}
            </div>
            {/* B21, said ONCE per group that has a collision in it and BELOW
                the chips: the reason is identical for every blocked option in
                the group, and repeating it on each one turns a picker read
                mid-service into a wall of warnings. The chip carries the same
                sentence as its `title` for a pointer, which a thumb has not
                got. */}
            {group.addons.some(
              (addon) => addon.isActive && blockedAddonIds?.has(addon.id),
            ) ? (
              <p
                className="text-xs font-semibold text-[color:var(--text-muted)]"
                data-testid={`addon-swap-collision-${group.id}`}
              >
                {swapCollisionNote}
              </p>
            ) : null}
          </fieldset>
        ))}

      </div>
      )}
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="addon-discard-prompt"
    />
    </>
  );
}

/**
 * Audit round 2 (5.1) — «(حتى 3)» beside a group's name when it caps the picks
 * above one, so the cap is read before the picker refuses the fourth tap. A
 * `maxSelect: 1` group says nothing (it is a radio, or a single optional
 * pick), and an uncapped one (`null`) has nothing to say. Both looks use it.
 */
function AddonGroupCap({ maxSelect }: { maxSelect: number | null }) {
  const t = useTranslate();
  if (maxSelect === null || maxSelect === 1) return null;
  return (
    <span data-ui="addon-cap" className="ms-1 font-normal tabular-nums">
      {t((m) => m.pos.addonUpTo, { max: String(maxSelect) })}
    </span>
  );
}

/**
 * PAPER — the picker's groups as the redesign draws them (`Restaurant
 * Dashboard.dc.html`, the cfg sheet): the size first, as cards (name, then
 * "Included" or "+EGP 4.00"; the chosen one a blue ring on a blue wash), then
 * the extras as checkbox rows — name at the start, "+EGP x" at the end. A row
 * draws no box of its own when picked: only its checkbox fills, and the focus
 * ring is the checkbox's, on keyboard focus alone. An option a guest may take
 * more than once grows a quiet "− n +" at the row's end once it is picked.
 */
function PaperPickerBody({
  groups,
  countOf,
  pickedIn,
  increase,
  decrease,
  clear,
  blockedAddonIds,
  swapCollisionNote,
  groupsMissing,
  note,
  handPrice,
  swapSet,
  onOverride,
  onSwap,
  onMarkSoldOut,
}: {
  groups: AddonGroup[];
  countOf: (addonId: string) => number;
  pickedIn: (group: AddonGroup) => number;
  increase: (group: AddonGroup, addonId: string) => void;
  decrease: (addonId: string) => void;
  clear: (addonId: string) => void;
  blockedAddonIds?: ReadonlySet<string>;
  swapCollisionNote: string;
  groupsMissing: boolean;
  note: string | null;
  handPrice: string | null;
  swapSet: boolean;
  onOverride?: () => void;
  onSwap?: () => void;
  onMarkSoldOut?: () => void;
}) {
  const t = useTranslate();
  const localName = useLocalName();
  const money = useMoney();
  return (
    <div data-ui="picker-body" className="flex flex-col">
      {groupsMissing ? (
        <p data-ui="picker-note" data-tone="danger">
          {t((m) => m.pos.addonsFailedTitle)}
        </p>
      ) : null}
      {paperGroupOrder(groups).map(({ group, shape }) => {
        const options = group.addons.filter((addon) => addon.isActive);
        const atCap =
          group.maxSelect !== null && pickedIn(group) >= group.maxSelect;
        return (
          <fieldset
            key={group.id}
            data-ui="addon-group"
            data-kind={group.kind}
            data-shape={shape}
            className="flex min-w-0 flex-col"
          >
            <legend data-ui="addon-legend">
              {localName(group)}
              <AddonGroupCap maxSelect={group.maxSelect} />
              {group.minSelect > 0 ? (
                <span data-ui="addon-required" className="ms-1">
                  {t((m) => m.pos.addonRequired)}
                </span>
              ) : null}
            </legend>
            {shape === "cards" ? (
              <div data-ui="addon-cards" className="grid">
                {options.map((addon) => {
                  const isOn = countOf(addon.id) > 0;
                  const collides = blockedAddonIds?.has(addon.id) ?? false;
                  return (
                    <button
                      key={addon.id}
                      type="button"
                      aria-pressed={isOn}
                      data-ui="addon-card"
                      data-on={isOn ? "" : undefined}
                      data-testid={`addon-${addon.id}`}
                      disabled={collides}
                      title={collides ? swapCollisionNote : undefined}
                      onClick={() =>
                        // A chosen size stays chosen when the group must have
                        // one; an optional version can be taken off again.
                        isOn
                          ? group.minSelect > 0
                            ? undefined
                            : decrease(addon.id)
                          : increase(group, addon.id)
                      }
                      className="flex flex-col items-center justify-center"
                    >
                      <span data-ui="addon-card-name">{localName(addon)}</span>
                      <span data-ui="addon-price" className="tabular-nums">
                        {addon.price > 0
                          ? `+${money(addon.price)}`
                          : t((m) => m.paperRegister.included)}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div data-ui="addon-rows" className="flex flex-col">
                {options.map((addon) => {
                  const taken = countOf(addon.id);
                  const isOn = taken > 0;
                  const collides = blockedAddonIds?.has(addon.id) ?? false;
                  // Only an option the group lets a guest take twice counts.
                  const repeatable = group.maxSelect !== 1;
                  const name = localName(addon);
                  return (
                    <div
                      key={addon.id}
                      data-ui="addon-row"
                      data-on={isOn ? "" : undefined}
                      className="flex items-center"
                    >
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={isOn}
                        data-ui="addon-row-toggle"
                        data-testid={`addon-${addon.id}`}
                        disabled={collides || (!isOn && atCap)}
                        title={collides ? swapCollisionNote : undefined}
                        onClick={() =>
                          isOn ? clear(addon.id) : increase(group, addon.id)
                        }
                        className="flex min-w-0 flex-1 items-center text-start"
                      >
                        <span data-ui="addon-check" aria-hidden="true" className="flex items-center justify-center">
                          {isOn ? <Check size={11} strokeWidth={2.25} /> : null}
                        </span>
                        <span data-ui="addon-row-name" className="min-w-0 flex-1 truncate">
                          {name}
                        </span>
                        {addon.price > 0 ? (
                          <span data-ui="addon-price" className="tabular-nums">
                            +{money(addon.price)}
                          </span>
                        ) : null}
                      </button>
                      {repeatable && isOn ? (
                        <span
                          role="group"
                          aria-label={t((m) => m.paperRegister.optionQuantity, { name })}
                          data-ui="addon-row-qty"
                          className="flex items-center"
                        >
                          <button
                            type="button"
                            aria-label={`${t((m) => m.pos.addonDecrease)} ${name}`}
                            data-ui="addon-row-step"
                            data-testid={`addon-minus-${addon.id}`}
                            onClick={() => decrease(addon.id)}
                            className="flex items-center justify-center"
                          >
                            −
                          </button>
                          <span
                            data-ui="addon-row-count"
                            data-testid={`addon-count-${addon.id}`}
                            className="text-center tabular-nums"
                          >
                            {taken}
                          </span>
                          <button
                            type="button"
                            aria-label={`${t((m) => m.pos.addonIncrease)} ${name}`}
                            data-ui="addon-row-step"
                            data-testid={`addon-plus-${addon.id}`}
                            disabled={atCap}
                            onClick={() => increase(group, addon.id)}
                            className="flex items-center justify-center"
                          >
                            +
                          </button>
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
            {options.some((addon) => blockedAddonIds?.has(addon.id)) ? (
              <p data-ui="picker-note" data-testid={`addon-swap-collision-${group.id}`}>
                {swapCollisionNote}
              </p>
            ) : null}
          </fieldset>
        );
      })}
      {onOverride || onSwap || onMarkSoldOut || handPrice || swapSet ? (
        // The acts that used to sit on every dish tile (D46, D61, D32), quiet
        // here: most plates never need either, and the people who may use them
        // are few (neither rung is in a preset below OWNER).
        <div data-ui="picker-actions" className="flex flex-wrap items-center">
          {handPrice ? (
            <span data-ui="picker-action-state" data-testid="addon-hand-price">
              {handPrice}
            </span>
          ) : null}
          {swapSet ? (
            <span data-ui="picker-action-state" data-testid="addon-swap-set">
              {t((m) => m.paperRegister.swapSet)}
            </span>
          ) : null}
          {onOverride ? (
            <button
              type="button"
              data-ui="picker-action"
              data-testid="addon-price-override"
              onClick={onOverride}
            >
              {t((m) => m.pos.priceOverrideAction)}
            </button>
          ) : null}
          {onSwap ? (
            <button
              type="button"
              data-ui="picker-action"
              data-testid="addon-swap"
              onClick={onSwap}
            >
              {swapSet ? t((m) => m.pos.swapAmend) : t((m) => m.pos.swapRemoveLabel)}
            </button>
          ) : null}
          {onMarkSoldOut ? (
            <button
              type="button"
              data-ui="picker-action"
              data-testid="addon-mark-sold-out"
              onClick={onMarkSoldOut}
            >
              {t((m) => m.pos.menuMarkSoldOutShort)}
            </button>
          ) : null}
        </div>
      ) : null}
      {note ? (
        <p data-ui="picker-note" data-tone="danger">
          {note}
        </p>
      ) : null}
    </div>
  );
}

/** PAPER — the foot: "− 1 +" for the dish's quantity, then the add. */
function PaperPickerFoot({
  withQuantity,
  quantity,
  onQuantity,
  label,
  disabled,
  onConfirm,
}: {
  withQuantity: boolean;
  quantity: number;
  onQuantity: (next: number) => void;
  label: string;
  disabled: boolean;
  onConfirm: () => void;
}) {
  const t = useTranslate();
  return (
    <>
      {withQuantity ? (
        <span
          role="group"
          aria-label={t((m) => m.paperRegister.quantity)}
          data-ui="picker-qty"
          className="flex items-center"
        >
          <button
            type="button"
            aria-label={t((m) => m.paperRegister.fewer)}
            data-ui="picker-step"
            data-testid="addon-qty-minus"
            disabled={quantity <= 1}
            onClick={() => onQuantity(Math.max(1, quantity - 1))}
            className="flex items-center justify-center"
          >
            −
          </button>
          <span
            data-ui="picker-qty-value"
            data-testid="addon-qty"
            aria-live="polite"
            className="text-center tabular-nums"
          >
            {quantity}
          </span>
          <button
            type="button"
            aria-label={t((m) => m.paperRegister.more)}
            data-ui="picker-step"
            data-testid="addon-qty-plus"
            disabled={quantity >= MAX_PLATES}
            onClick={() => onQuantity(Math.min(MAX_PLATES, quantity + 1))}
            className="flex items-center justify-center"
          >
            +
          </button>
        </span>
      ) : null}
      <Button
        variant="primary"
        size="touch"
        disabled={disabled}
        onClick={onConfirm}
        data-testid="addon-confirm"
      >
        {label}
      </Button>
    </>
  );
}

/** The same picks, ignoring entries at zero. */
function samePicks(a: Record<string, number>, b: Record<string, number>): boolean {
  const live = (picks: Record<string, number>) =>
    Object.entries(picks).filter(([, count]) => count > 0);
  const left = live(a);
  const right = live(b);
  return left.length === right.length && left.every(([id, count]) => b[id] === count);
}
